import React, { Fragment, useEffect, useState } from "react";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { Btn, H4, Image, P } from "../AbstractElements";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "react-feather";
import credoraToast from "../Components/Credora/Common/credoraToast";
import { acceptInvitation, fetchInvitationPreview } from "../api/auth";
import { brandLogo } from "../assets/brand/credoraLogos";
import authIllustration from "../assets/images/login/auth-img.svg";

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing invitation link. Ask your administrator to resend the invite.");
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchInvitationPreview(token)
      .then((data) => setPreview(data))
      .catch((err) => {
        setError(err.response?.data?.detail || "This invitation link is invalid or has expired.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const passwordsMatch = password === passwordConfirm;
  const canSubmit =
    token &&
    preview &&
    password.length >= 8 &&
    passwordConfirm.length >= 8 &&
    passwordsMatch &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvitation({ token, password, password_confirm: passwordConfirm });
      credoraToast.success("Password created. You can sign in now.");
      navigate(`${process.env.PUBLIC_URL}/login`);
    } catch (err) {
      credoraToast.error(err.response?.data?.detail, "Could not complete account setup.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div className="credora-login">
        <div className="credora-login__shell">
          <aside className="credora-login__brand" aria-hidden="true">
            <div className="credora-login__brand-inner">
              <p className="credora-login__eyebrow">Staff onboarding</p>
              <h1 className="credora-login__headline">
                Create your password
                <span> to access Credora360</span>
              </h1>
              <P attrPara={{ className: "credora-login__tagline" }}>
                Use the secure link from your invitation email to finish setting up your account.
              </P>
              <img src={authIllustration} alt="" className="credora-login__illustration" />
            </div>
          </aside>

          <main className="credora-login__panel">
            <div className="credora-login__panel-inner">
              <div className="credora-login__logo-wrap">
                <Image attrImage={{ className: "img-fluid", src: brandLogo, alt: "Credora360" }} />
              </div>
              <H4 attrH4={{ className: "credora-login__title" }}>Set up your account</H4>

              {loading ? (
                <P>Verifying invitation…</P>
              ) : error ? (
                <>
                  <P attrPara={{ className: "text-danger" }}>{error}</P>
                  <Link to={`${process.env.PUBLIC_URL}/login`} className="btn btn-primary btn-block mt-3">
                    Back to sign in
                  </Link>
                </>
              ) : (
                <Form className="theme-form credora-login__form" onSubmit={handleSubmit}>
                  <P attrPara={{ className: "text-muted small mb-3" }}>
                    Setting up <strong>{preview.full_name}</strong> ({preview.email})
                  </P>
                  <FormGroup>
                    <Label className="col-form-label">New password</Label>
                    <div className="credora-login__input-wrap">
                      <span className="credora-login__input-icon" aria-hidden="true">
                        <Lock size={18} />
                      </span>
                      <Input
                        type={togglePassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={8}
                        required
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        className="credora-login__toggle-pw"
                        onClick={() => setTogglePassword((v) => !v)}
                        aria-label={togglePassword ? "Hide password" : "Show password"}>
                        {togglePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label className="col-form-label">Confirm password</Label>
                    <div className="credora-login__input-wrap">
                      <span className="credora-login__input-icon" aria-hidden="true">
                        <Lock size={18} />
                      </span>
                      <Input
                        type={togglePassword ? "text" : "password"}
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        minLength={8}
                        required
                        autoComplete="new-password"
                        placeholder="Retype password"
                      />
                    </div>
                    {passwordConfirm && !passwordsMatch && (
                      <small className="text-danger">Passwords do not match.</small>
                    )}
                  </FormGroup>
                  <Btn
                    attrBtn={{
                      className: "btn-block credora-login__submit",
                      color: "primary",
                      type: "submit",
                      disabled: !canSubmit,
                    }}>
                    {submitting ? "Saving…" : "Create password & continue"}
                  </Btn>
                  <P attrPara={{ className: "credora-login__footer text-center mt-3 mb-0" }}>
                    Already set up?{" "}
                    <Link to={`${process.env.PUBLIC_URL}/login`}>Sign in</Link>
                  </P>
                </Form>
              )}
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  );
};

export default SetupAccount;
